/**
 * Maps the backend's app-wide {@code BookingErrorCode} (the stable contract in
 * `BookingErrorCode.java`) to an i18n key under `booking.errors.*`. The backend
 * owns the code + a human English message; the frontend localizes by code and
 * shows a generic localized message for any code it doesn't recognize yet
 * (backend text never reaches the UI).
 *
 * Kept in the shared lib so the marketplace and the per-tenant client-booking
 * SPA localize identically. The catalogs in each app define these keys.
 */

/** Shape of the backend error body ({@code BookingExceptionMapper}). */
export interface BookingErrorBody {
  code?: string;
  message?: string;
  details?: Record<string, unknown> | null;
  retryable?: boolean;
  /**
   * A machine reason the backend sent as `message` (lower_snake, e.g.
   * `outside_business_hours` with BOOKING_RULE_VIOLATION). Safe to keep: it is
   * a key, not human text, and it is never rendered.
   */
  reason?: string;
}

const MACHINE_REASON = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/;

/** `value` when it is a lower_snake machine reason ("outside_business_hours"), else undefined. */
export function machineReason(value: unknown): string | undefined {
  return typeof value === 'string' && value.length <= 64 && MACHINE_REASON.test(value) ? value : undefined;
}

/**
 * code → i18n key. Any code NOT listed here (or a missing code) resolves to
 * `null`, signalling the caller to fall back to the backend message. Retryable
 * slot-race codes and hard-stop eligibility codes both map here; whether to
 * show a retry affordance is driven by `retryable` on the body, not the key.
 */
const CODE_TO_KEY: Record<string, string> = {
  TIME_SLOT_NO_LONGER_AVAILABLE: 'booking.errors.timeSlotUnavailable',
  EMPLOYEE_UNAVAILABLE: 'booking.errors.employeeUnavailable',
  LOCATION_UNAVAILABLE: 'booking.errors.locationUnavailable',
  BOOKING_RULE_VIOLATION: 'booking.errors.bookingRuleViolation',
  SERVICE_NOT_BOOKABLE: 'booking.errors.serviceNotBookable',
  ONLINE_BOOKING_DISABLED: 'booking.errors.onlineDisabled',
  MOBILE_BOOKING_DISABLED: 'booking.errors.mobileDisabled',
  CUSTOMER_ADDRESS_REQUIRED: 'booking.errors.addressRequired',
  CUSTOMER_TIMEZONE_REQUIRED: 'booking.errors.timezoneRequired',
  PAYMENT_REQUIRED: 'booking.errors.paymentRequired',
  PAYMENT_FAILED: 'booking.errors.paymentFailed',
  PAYMENT_REFUNDED: 'booking.errors.paymentRefunded',
  PAYMENT_CANCELLED: 'booking.errors.paymentCancelled',
  STRIPE_ACCOUNT_NOT_READY: 'booking.errors.stripeAccountNotReady',
  FEATURE_REQUIRES_UPGRADE: 'booking.errors.featureRequiresUpgrade',
  PLAN_LIMIT_REACHED: 'booking.errors.planLimitReached',
  INVALID_REQUEST: 'booking.errors.generic',
  INTERNAL_ERROR: 'booking.errors.generic',
};

/** The i18n key for a backend error code, or null if the code is unknown. */
export function bookingErrorKey(code: string | undefined | null): string | null {
  if (!code) return null;
  return CODE_TO_KEY[code] ?? null;
}

/**
 * Parses a thrown booking error into `{code, details, retryable}` (+ `message`
 * only for human text). Never returns a backend body's text:
 *   - `BookingApiError` (or anything with a string `code`) → its parsed fields.
 *   - A JSON string (the safe `{code,…}` the payment step hands to `onError`,
 *     or a legacy raw body) → code/details/retryable; any backend `message` /
 *     `error` text in it is dropped.
 *   - Any other string (the step's own localized labels, Stripe's
 *     customer-facing card messages) → `{ message }`, shown as is.
 */
export function parseBookingError(err: unknown): BookingErrorBody {
  if (err && typeof err === 'object' && typeof (err as { code?: unknown }).code === 'string') {
    const e = err as BookingErrorBody;
    return pickSafe(e);
  }
  if (err && typeof err === 'object' && typeof (err as { status?: unknown }).status === 'number') {
    // An HTTP error without a code: nothing safe to show but the generic key.
    return {};
  }
  const raw =
    err && typeof err === 'object' && 'message' in err
      ? String((err as { message?: unknown }).message ?? '')
      : String(err ?? '');
  const trimmed = raw.trim();
  if (trimmed.startsWith('{')) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object') return pickSafe(parsed as BookingErrorBody);
    } catch {
      return {};
    }
  }
  return { message: trimmed || undefined };
}

function pickSafe(body: BookingErrorBody): BookingErrorBody {
  const out: BookingErrorBody = {};
  if (typeof body.code === 'string' && body.code) out.code = body.code;
  if (body.details && typeof body.details === 'object') out.details = body.details;
  if (typeof body.retryable === 'boolean') out.retryable = body.retryable;
  const reason = machineReason(body.reason) ?? machineReason(body.message);
  if (reason) out.reason = reason;
  return out;
}

/**
 * The string the payment step passes to `onError` for an API failure: only the
 * parsed code/retryable/details as JSON, which `parseBookingError` understands.
 */
export function safeBookingErrorString(err: unknown): string {
  return JSON.stringify(parseBookingError(err && typeof err === 'object' ? err : {}));
}

/**
 * A translate function compatible with i18next's `t`. We call it as
 * `t(key, { defaultValue })` — the overload i18next actually exposes — so this
 * type is written to accept the app's `t` from `useTranslation()` directly.
 * The return is coerced to string (i18next's return type is broad).
 */
export type TranslateFn = (
  key: string,
  options?: { defaultValue?: string },
) => unknown;

const GENERIC_DEFAULT = 'Something went wrong. Please try again.';

/**
 * Resolves the user-facing message for a booking error using a translate
 * function `t`: the localized string for a known code; the generic key for an
 * unknown code (never the backend's text); a human `message` (localized step
 * labels, Stripe card messages) only when there is no code at all.
 */
export function localizeBookingError(
  body: BookingErrorBody,
  t: TranslateFn,
): string {
  const generic = String(t('booking.errors.generic', { defaultValue: GENERIC_DEFAULT }));
  const key = bookingErrorKey(body.code);
  if (key) return String(t(key, { defaultValue: generic }));
  if (body.code) return generic;
  if (body.message) return body.message;
  return generic;
}
